import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  untracked,
  input,
  model,
  Optional,
  output,
  viewChild,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  InputNumberComponent,
  InputRadioGroupComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { AlertComponent, TagComponent } from '@pedix-workspace/angular-ui-alert';
import { getPaymentMethodValue } from '@pedix-workspace/shared-models';
import {
  PaymentMethod,
  PaymentMethodType,
  EndOrderDetailDigitalPayment,
  InstallmentOption,
  isPaymentMethodTransfer,
  isPaymentMethodCash,
  isPaymentMethodWithLink,
  TransferPayer,
} from '@pedix-workspace/utils';
import { IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import { ApiV1_AvailableIntegration } from '@pedix-workspace/api-v1';
import { EndOrderBankTransferFieldsComponent } from '../end-order-bank-transfer-fields/end-order-bank-transfer-fields.component';

@Component({
  selector: 'pxw-end-order-payment-methods-fields',
  templateUrl: './end-order-payment-methods-fields.component.html',
  styleUrls: ['./end-order-payment-methods-fields.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    FormsModule,
    InputRadioGroupComponent,
    TagComponent,
    AlertComponent,
    IconInfoComponent,
    InputNumberComponent,
    EndOrderBankTransferFieldsComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class EndOrderPaymentMethodsFieldsComponent {
  private readonly bankTransferFields = viewChild(EndOrderBankTransferFieldsComponent);
  private readonly t = inject(TranslocoService);

  readonly paymentMethods = input.required<PaymentMethod[]>();
  readonly restrictionHint = input<string | null>(null);

  readonly cvuLoading = computed(() => this.bankTransferFields()?.cvuLoading() ?? false);

  readonly isFormSubmitted = input<boolean>(false);
  readonly orderFinalAmount = input<number>(0);
  readonly displayLinkInfo = input<boolean>(false);
  readonly displayHowMuchField = input<boolean>(false);

  // New inputs for bank transfer integration
  readonly establishmentId = input.required<string>();
  readonly customerName = input.required<string>();
  readonly availableIntegrations = input<ApiV1_AvailableIntegration[]>([]);
  readonly showTransferDetails = input<boolean>(false);
  readonly transferReferenceId = input<string>();
  readonly payerDocumentEnabled = input<boolean>(false);
  readonly transferPayer = model<TransferPayer | null>(null);

  // Outputs
  readonly digitalPaymentChange = output<EndOrderDetailDigitalPayment | null>();

  readonly paymentMethod = model.required<PaymentMethod | null>();
  readonly howMuch = model.required<number | null>();
  readonly selectedInstallments = model<number | undefined>(undefined);

  readonly shouldDisplayHowMuchField = computed(() => {
    const pm = this.paymentMethod();
    return (
      this.displayHowMuchField() &&
      pm !== null &&
      isPaymentMethodCash(pm) &&
      pm.displayCashPaymentAmount
    );
  });

  readonly shouldDisplayTransferFields = computed(() => {
    const pm = this.paymentMethod();
    return pm !== null && isPaymentMethodTransfer(pm);
  });

  readonly selectedPaymentMethodIsLink = computed(() => {
    const pm = this.paymentMethod();
    return pm !== null && isPaymentMethodWithLink(pm);
  });

  readonly linkPaymentMethod = computed(() => {
    const pm = this.paymentMethod();
    if (pm !== null && isPaymentMethodWithLink(pm)) {
      return pm;
    }
    return null;
  });

  readonly transferPaymentMethod = computed(() => {
    const pm = this.paymentMethod();
    if (pm !== null && isPaymentMethodTransfer(pm)) {
      return pm;
    }
    return null;
  });

  readonly installmentOptions = computed(() => {
    const pm = this.paymentMethod();
    if (pm === null || !pm.installmentOptions?.length) {
      return undefined;
    }
    return pm.installmentOptions;
  });

  readonly installmentRadioOptions = computed(() => {
    const options = this.installmentOptions();
    if (!options) return undefined;

    const allOptions = options.some(o => o.installments === 1)
      ? options
      : [{ installments: 1, surchargePercent: 0 } as InstallmentOption, ...options];

    const ofLabel = this.t.translate('endOrder.installmentsOf');
    return [...allOptions]
      .sort((a, b) => a.installments - b.installments)
      .map(o => ({
        value: o.installments,
        label:
          o.installments === 1
            ? this.t.translate('endOrder.installmentsSinglePayment')
            : `${o.installments} ${ofLabel}`,
        surcharge: o.surchargePercent,
      }));
  });

  constructor() {
    effect(() => {
      const paymentMethod = this.paymentMethod();
      const paymentMethods = this.paymentMethods();

      if (
        paymentMethod &&
        !paymentMethods.find(
          pm => getPaymentMethodValue(pm) === getPaymentMethodValue(paymentMethod),
        )
      ) {
        this.paymentMethod.set(null);
      }
    });

    let wasTransfer = false;
    effect(() => {
      const pm = this.paymentMethod();
      const isTransfer = pm !== null && isPaymentMethodTransfer(pm);
      if (wasTransfer && !isTransfer) {
        this.digitalPaymentChange.emit(null);
      }
      wasTransfer = isTransfer;
    });

    effect(() => {
      const pm = this.paymentMethod();
      const next = pm !== null && !!pm.installmentOptions?.length ? 1 : undefined;
      if (untracked(() => this.selectedInstallments()) !== next) {
        this.selectedInstallments.set(next);
      }
    });
  }

  onDigitalPaymentChange(digitalPayment: EndOrderDetailDigitalPayment | null): void {
    this.digitalPaymentChange.emit(digitalPayment);
  }

  /**
   * Confirms the bank transfer amount before order submission.
   * Only applicable for ChytaPay integration.
   */
  async confirmBankTransferAmount(): Promise<void> {
    await this.bankTransferFields()?.confirmAmount();
  }
}
